import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { ClipboardList, Plus, Eye, Trash2, ArrowLeft, FileText, Upload, Download } from 'lucide-react';

const AdminForms = ({ user, setUser }) => {
  const navigate = useNavigate();
  
  // Custom Forms state
  const [forms, setForms] = useState([]);
  const [showCreateFormDialog, setShowCreateFormDialog] = useState(false);
  const [showFormSubmissionsDialog, setShowFormSubmissionsDialog] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formSubmissions, setFormSubmissions] = useState([]);
  
  // PDF Forms state
  const [pdfForms, setPdfForms] = useState([]);
  const [showCreatePdfFormDialog, setShowCreatePdfFormDialog] = useState(false);
  const [showPdfSubmissionsDialog, setShowPdfSubmissionsDialog] = useState(false);
  const [selectedPdfForm, setSelectedPdfForm] = useState(null);
  const [pdfSubmissions, setPdfSubmissions] = useState([]);
  const [pdfFormData, setPdfFormData] = useState({
    title: '',
    description: '',
    companyId: ''
  });
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);

  useEffect(() => {
    loadForms();
    loadPdfForms();
  }, []);

  const loadForms = async () => {
    try {
      const response = await axiosInstance.get('/admin/forms');
      setForms(response.data);
    } catch (error) {
      console.error('Failed to load forms:', error);
    }
  };

  const loadPdfForms = async () => {
    try {
      const response = await axiosInstance.get('/pdf-forms');
      setPdfForms(response.data);
    } catch (error) {
      console.error('Failed to load PDF forms:', error);
    }
  };

  const handleViewFormSubmissions = async (formId) => {
    try {
      const response = await axiosInstance.get(`/admin/forms/${formId}/submissions`);
      setFormSubmissions(response.data);
      setShowFormSubmissionsDialog(true);
    } catch (error) {
      console.error('Failed to load form submissions:', error);
      toast.error('Failed to load form submissions');
    }
  };

  const handleViewPdfSubmissions = async (formId) => {
    try {
      const response = await axiosInstance.get(`/pdf-forms/${formId}/submissions`);
      setPdfSubmissions(response.data);
      setShowPdfSubmissionsDialog(true);
    } catch (error) {
      console.error('Failed to load PDF form submissions:', error);
      toast.error('Failed to load PDF submissions');
    }
  };

  const handleCreatePdfForm = async (e) => {
    e.preventDefault();
    if (!selectedPdfFile) {
      toast.error('Please select a PDF file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', selectedPdfFile);
      formData.append('title', pdfFormData.title);
      formData.append('description', pdfFormData.description);

      await axiosInstance.post('/pdf-forms', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('PDF form created successfully');
      setShowCreatePdfFormDialog(false);
      setPdfFormData({ title: '', description: '', companyId: '' });
      setSelectedPdfFile(null);
      loadPdfForms();
    } catch (error) {
      console.error('Failed to create PDF form:', error);
      toast.error('Failed to create PDF form');
    }
  };

  const handleDeletePdfForm = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;

    try {
      await axiosInstance.delete(`/pdf-forms/${id}`);
      toast.success('PDF form deleted');
      loadPdfForms();
    } catch (error) {
      console.error('Failed to delete PDF form:', error);
      toast.error('Failed to delete PDF form');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* SafetyMind AI Branding */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-lg flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-lg">SM</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">SafetyMind AI</h2>
              <p className="text-xs text-gray-500 italic">Safety That Thinks</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Company Forms</h1>
          <p className="text-gray-600 mt-1">Manage custom forms and PDF forms (W-4, I-9, etc.)</p>
        </div>

        <Tabs defaultValue="federal-state-forms" className="space-y-6">
          <TabsList>
            <TabsTrigger value="federal-state-forms">
              <FileText className="w-4 h-4 mr-2" />
              Federal & State Forms
            </TabsTrigger>
            <TabsTrigger value="pdf-forms">
              <FileText className="w-4 h-4 mr-2" />
              Company PDF Forms
            </TabsTrigger>
            <TabsTrigger value="custom-forms">
              <ClipboardList className="w-4 h-4 mr-2" />
              Custom Forms
            </TabsTrigger>
          </TabsList>

          {/* Federal & State Forms Tab */}
          <TabsContent value="federal-state-forms" className="space-y-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Common federal and state employment forms for workers to complete
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Federal Forms</CardTitle>
                <CardDescription>IRS and Department of Labor forms</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* W-4 Form */}
                  <Card className="border-2 hover:border-blue-300 transition-colors">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        W-4 Form
                      </CardTitle>
                      <CardDescription className="text-xs">Federal Tax Withholding</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-3">
                        Employee's Withholding Certificate - Required for all new hires to determine federal income tax withholding.
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="w-full">
                          <Eye className="w-4 h-4 mr-1" />
                          View Form
                        </Button>
                        <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700">
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* I-9 Form */}
                  <Card className="border-2 hover:border-green-300 transition-colors">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-green-600" />
                        I-9 Form
                      </CardTitle>
                      <CardDescription className="text-xs">Employment Eligibility Verification</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-3">
                        Employment Eligibility Verification - Required by law for all U.S. employees to verify identity and employment authorization.
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="w-full">
                          <Eye className="w-4 h-4 mr-1" />
                          View Form
                        </Button>
                        <Button size="sm" className="w-full bg-green-600 hover:bg-green-700">
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* W-9 Form */}
                  <Card className="border-2 hover:border-purple-300 transition-colors">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-purple-600" />
                        W-9 Form
                      </CardTitle>
                      <CardDescription className="text-xs">Request for Taxpayer Identification</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-3">
                        For independent contractors and vendors to provide their TIN for 1099 reporting purposes.
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="w-full">
                          <Eye className="w-4 h-4 mr-1" />
                          View Form
                        </Button>
                        <Button size="sm" className="w-full bg-purple-600 hover:bg-purple-700">
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Direct Deposit Form */}
                  <Card className="border-2 hover:border-orange-300 transition-colors">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-orange-600" />
                        Direct Deposit
                      </CardTitle>
                      <CardDescription className="text-xs">Electronic Payment Authorization</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-3">
                        Authorization form for employees to set up direct deposit for payroll to their bank account.
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="w-full">
                          <Eye className="w-4 h-4 mr-1" />
                          View Form
                        </Button>
                        <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700">
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            {/* State Forms Section */}
            <Card>
              <CardHeader>
                <CardTitle>State Forms</CardTitle>
                <CardDescription>State-specific employment and tax forms</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* State Withholding */}
                  <Card className="border-2 hover:border-indigo-300 transition-colors">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-indigo-600" />
                        State Withholding
                      </CardTitle>
                      <CardDescription className="text-xs">State Tax Withholding Certificate</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-3">
                        State-specific tax withholding forms. Select your state to download the appropriate form.
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="w-full">
                          <Eye className="w-4 h-4 mr-1" />
                          Select State
                        </Button>
                        <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700">
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* State New Hire Reporting */}
                  <Card className="border-2 hover:border-teal-300 transition-colors">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-teal-600" />
                        New Hire Reporting
                      </CardTitle>
                      <CardDescription className="text-xs">State New Hire Registry</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-3">
                        Required reporting to state for new employees within 20 days of hire date.
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="w-full">
                          <Eye className="w-4 h-4 mr-1" />
                          Select State
                        </Button>
                        <Button size="sm" className="w-full bg-teal-600 hover:bg-teal-700">
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Company PDF Forms Tab */}
          <TabsContent value="pdf-forms" className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Manage fillable PDF forms like W-4, I-9, and other government forms
              </p>
              <Button onClick={() => setShowCreatePdfFormDialog(true)} className="gap-2">
                <Plus className="w-4 h-4" />
                Upload PDF Form
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>PDF Forms ({pdfForms.length})</CardTitle>
                <CardDescription>Fillable PDF documents for workers</CardDescription>
              </CardHeader>
              <CardContent>
                {pdfForms.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600">No PDF forms yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Submissions</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pdfForms.map((form) => (
                        <TableRow key={form.id}>
                          <TableCell className="font-medium">{form.title}</TableCell>
                          <TableCell className="text-sm text-gray-600">{form.description}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {form.submissionCount || 0} submissions
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedPdfForm(form);
                                  handleViewPdfSubmissions(form.id);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`/api/pdf-forms/${form.id}/download`, '_blank')}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeletePdfForm(form.id, form.title)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminForms;
